import React, { useState, useEffect, useRef, useCallback } from "react";
import { v4 as uuidv4 } from 'uuid';

import {
    Box,
    Typography,
    IconButton,
    CircularProgress,
    // Grid,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    // Card,
    // CardContent,
    Avatar,
    Stack,
    Container,
    Divider,
    Dialog,
    DialogContent,
    DialogActions,
    // Button,
    Fade,
    // Backdrop,
} from "@mui/material";
import {
    VolumeUp,
    Mic,
    // ScreenShare,
    CheckCircle,
    // Language,
    PlayArrow,
    MicOff,
    Replay,
    ArrowForward,
    SmartToy,
    RecordVoiceOver,
    // VolumeOff,
    Stop,
    Timer,
    Speed
} from "@mui/icons-material";

import CustomCard from "../../components/common/CustomCard";
import CustomButton from "../../components/common/CustomButton";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import { useNavigate } from "react-router-dom";
// import { color } from "framer-motion";
import { DotsLoader } from "../../components/common/spinLoader";
import VideoRecorderModal from "../../components/common/VideoModule";
// import Footer from "../newComponents/CustomFooter";
import cybermeru from '../../assets/images/merubot_logo.png';
// import cybermeru2 from '../../assets/images/merubot_icon.png';
import { interviewApi } from "../../api/interviewApi";
import { questionApi } from "../../api/questionanswerApi";
import { useAuth } from "../../contexts/AuthContext";


// Constants
const ACCENT_OPTIONS = {
    "en-US": "US English",
    "en-GB": "UK English",
    "en-IN": "Indian English"
};

const VOICE_PRIORITIES = {
    'en-US': ['Google US English', 'Microsoft Zira', 'Microsoft David'],
    'en-GB': ['Google UK English Female', 'Google UK English Male', 'Microsoft Hazel'],
    'en-IN': ['Google Indian English', 'Microsoft Ravi']
};

const CONVERSATION_INTROS = [
    "Let's start with: ",
    "Next, I'd like to ask: ",
    "Here's an interesting question: ",
    "Now, please tell me: ",
    "I'm curious about: ",
    "Can you help me understand: "
];

// ─── Language Validation ──────────────────────────────────────────────────────
// Layer 1: Curated set of common Hindi/Marathi romanized words (code-mix detection)
const HINDI_MARATHI_WORDS = new Set([
    // Common Hindi function words & pronouns
    "aap", "main", "mein", "hain", "hai", "tha", "thi", "the",
    "karo", "karna", "karen", "kiya", "kiye", "kijiye",
    "yeh", "woh", "waha", "yaha", "aur", "lekin", "toh", "par",
    "pahle", "pehle", "baad", "phir", "fir", "ab", "abhi",
    "nahi", "nahin", "hoga", "hoge", "hoti", "hota",
    "matlab", "samajh", "samjha", "batao", "bataye", "bata",
    "iska", "uska", "inka", "unka", "mera", "tera", "apna",
    "bahut", "thoda", "zyada", "kam", "achha", "accha",
    "kyun", "kyu", "kaise", "kya", "kaisa", "kaun",
    "isliye", "isiliye", "jaisa", "waisa", "jab", "tab",
    "phir", "dobara", "wapas", "seedha", "seedhe",
    // Common Marathi function words & verbs
    "aahe", "ahe", "asate", "asto", "naahi",
    "mala", "tula", "tyala", "amhi", "aamhi", "aapan",
    "kaay", "kay", "kuthe", "kasa", "kashi", "kashe",
    "ghya", "dya", "sanga", "sangto", "sangtat", "aahet",
    "marathakil", "matyanantar", "tyachavarti", "madesh",
    "aahe", "naahi", "hoil", "hote", "hotey", "zala", "zale",
    // Common romanized connectors/postpositions (used heavily in code-mix)
    "wala", "wali", "wale", "ke", "ki", "ka", "se", "ne",
    "pe", "ko", "tak", "bhi", "tho", "liye", "liye",
]);

/**
 * Layer 1: Detect non-Latin scripts (Devanagari, Arabic, CJK, Cyrillic, etc.)
 * Allows ASCII (0x00-0x7F) and extended Latin (0xC0-0x24F) for names/tech terms.
 */
const containsNonLatinScript = (text) => {
    // Block anything outside ASCII + extended Latin range
    return /[\u0080-\u00BF\u0250-\uFFFF]/.test(text);
};

/**
 * Layer 2: Detect romanized Hindi/Marathi code-mixing in Latin script.
 * Flags if 2+ known words match, or >15% of words match (for longer responses).
 */
const containsHindiMarathiWords = (text) => {
    const words = text.toLowerCase().replace(/[^a-z\s]/g, " ").split(/\s+/).filter(Boolean);
    if (words.length === 0) return false;
    const matches = words.filter(w => HINDI_MARATHI_WORDS.has(w));
    return matches.length >= 2 || (words.length > 4 && (matches.length / words.length) > 0.15);
};

/**
 * Combined English-only guard: returns false if non-Latin script OR code-mix detected.
 */
const isAnswerEnglishOnly = (text) => {
    if (!text || !text.trim()) return true; // empty handled elsewhere
    if (containsNonLatinScript(text)) return false;
    if (containsHindiMarathiWords(text)) return false;
    return true;
};


const InterviewTimer = ({ startTime, isPaused, currentQuestionTime, onQuestionTimeUpdate }) => {
    const [totalTime, setTotalTime] = useState(0);
    const [questionTime, setQuestionTime] = useState(0);


    useEffect(() => {
        const interval = setInterval(() => {
            if (!isPaused && startTime) {
                const elapsed = Math.floor((Date.now() - startTime) / 1000);
                setTotalTime(elapsed);

                const qTime = Math.floor((Date.now() - currentQuestionTime) / 1000);
                setQuestionTime(qTime);
                onQuestionTimeUpdate?.(qTime);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [isPaused, startTime, currentQuestionTime, onQuestionTimeUpdate]);



    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const getTimeColor = (time) => {
        if (time < 120) return 'success.main';
        if (time < 300) return 'warning.main';
        return 'error.main';
    };

    return (
        <Paper sx={{ p: 2, bgcolor: '#f8f9ff', borderRadius: 2, border: '1px solid #e3f2fd' }}>
            <Stack direction="row" spacing={3} alignItems="center" justifyContent="center">
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Timer sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Box textAlign="center">
                        <Typography variant="h6" fontWeight={600} color="primary">
                            {formatTime(totalTime)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Total Time
                        </Typography>
                    </Box>
                </Stack>

                <Divider orientation="vertical" flexItem />

                <Stack direction="row" alignItems="center" spacing={1}>
                    <Speed sx={{ color: getTimeColor(questionTime), fontSize: 20 }} />
                    <Box textAlign="center">
                        <Typography variant="h6" fontWeight={600} sx={{ color: getTimeColor(questionTime) }}>
                            {formatTime(questionTime)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Question Time
                        </Typography>
                    </Box>
                </Stack>
            </Stack>
        </Paper>
    );
};

const InterviewPage = () => {
    // Core state
    const { upload_id, interview_status, candidate_id, recruiter_id } = useAuth();

    const [questions, setQuestions] = useState([]);
    const [questionCount, setQuestionCount] = useState(0)
    const [currentIdx, setCurrentIdx] = useState(0);
    const [answer, setAnswer] = useState("");
    const [selectedAccent, setSelectedAccent] = useState("en-US");
    const [listening, setListening] = useState(false);
    const [isQuestionPlaying, setIsQuestionPlaying] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isPlayingAudio, setIsPlayingAudio] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });
    const [conversationState, setConversationState] = useState('listening');
    // const [interviewStartTime] = useState(Date.now());
    const [currentQuestionStartTime, setCurrentQuestionStartTime] = useState(Date.now());
    const [isInterviewCompleted, setIsInterviewCompleted] = useState(false);
    const [voicesLoaded, setVoicesLoaded] = useState(false);
    const [continueLoader, setContinueLoader] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);
    const [destroyVideoModule, setDestroyVideoModule] = useState(false);

    const [showRefreshDialog, setShowRefreshDialog] = useState(false);
    const [isFinalizingForRefresh, setIsFinalizingForRefresh] = useState(false);

    const hasFetchedRef = useRef(false);
    const recognitionRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioBlobRef = useRef(null);
    const speechTimeoutRef = useRef(null);
    const currentUtteranceRef = useRef(null);
    const audioRef = useRef(null); // for playback control
    // Heartbeat and inactivity tracking refs
    const heartbeatIntervalRef = useRef(null);
    const heartbeatStartTimeoutRef = useRef(null);
    const inactivityTimeoutRef = useRef(null);


    // Session data (handled by AuthContext)
    const navigate = useNavigate();

    // Utilities
    const showSnackbar = (message, type) => {
        setSnackbar({ open: true, message, type });
    };

    useEffect(() => {
        const loadVoices = () => {
            const synth = window.speechSynthesis;
            const voices = synth.getVoices();
            if (voices.length > 0) {
                setVoicesLoaded(true);
            }
        };

        // Load voices immediately
        loadVoices();

        // Some browsers need this event
        if (window.speechSynthesis.onvoiceschanged !== undefined) {
            window.speechSynthesis.onvoiceschanged = loadVoices;
        }

        return () => {
            if (window.speechSynthesis.onvoiceschanged !== undefined) {
                window.speechSynthesis.onvoiceschanged = null;
            }
        };
    }, []);

    const fetchQuestions = async () => {
        try {
            const interviewStatus = interview_status?.toLowerCase();

            /* ─────────── STARTED / SCHEDULED────────── */
            if (interviewStatus === "started" || interviewStatus === "scheduled") {
                const response = await questionApi.retrieveMatchingQA();
                // console.log("Retrieve QA response:", response);
                const { status, message, data } = response || {};

                // Network error
                if (status === 0) {
                    showSnackbar(
                        "Network error. Please check your internet connection.",
                        "warning"
                    );
                    return;
                }

                // Server / client error
                if (status >= 400) {
                    showSnackbar(
                        message || "Failed to load interview questions.",
                        "error"
                    );
                    return;
                }

                // Success
                if (status === 200 && Array.isArray(data)) {
                    setQuestions(data);
                    setQuestionCount(data.length);

                    const questionText = getConversationalQuestion(
                        data[currentIdx]?.question,
                        currentIdx
                    );

                    setTimeout(() => speakText(questionText), 1000);
                    return;
                }

                showSnackbar("Unexpected response while loading questions.", "error");
                return;
            }

            /* ───────────  INTERRUPTED (RESUME) ────────── */
            if (interviewStatus === "interrupted") {
                const response = await interviewApi.resume();
                // console.log("Resume interview response:", response);

                const { status, message, data } = response || {};
                const questions = data?.questions;

                // Network error
                if (status === 0) {
                    showSnackbar(
                        "Network error. Please check your internet connection.",
                        "warning"
                    );
                    return;
                }

                // Error
                if (status >= 400) {
                    showSnackbar(
                        message || "Failed to resume interview.",
                        "error"
                    );
                    return;
                }

                // Success
                if (status === 200 && Array.isArray(questions)) {
                    setQuestions(questions);
                    setQuestionCount(questions.length);
                    if (!questions.length) {
                        showSnackbar(
                            "No interview questions available.",
                            "warning"
                        )
                    }
                    const questionText = getConversationalQuestion(
                        questions[currentIdx]?.question,
                        currentIdx
                    );
                    setTimeout(() => speakText(questionText), 1000);
                    return;
                }
                showSnackbar("Unexpected response while resuming interview.", "error");
            }
        }
        catch (err) {
            console.error("Error fetching questions:", err);
            showSnackbar(
                "Something went wrong while loading questions.",
                "error"
            );
        }
    };


    const finalizeInterview = async () => {
        // 0. KILL CAMERA *INSTANTLY* before any other async work
        try {
            if (window.activeInterviewStream) {
                window.activeInterviewStream.getTracks().forEach(t => t.stop());
                window.activeInterviewStream = null;
            }

            const videoEl = document.querySelector("video");
            if (videoEl) {
                videoEl.pause();
                videoEl.srcObject = null;
                // console.log("🔥 VIDEO ELEMENT CLEARED");
            }


        } catch (err) {
            console.warn("Critical camera kill failed:", err);
        }
        try {
            // console.log("FINALIZE INTERVIEW CALLED → STOP EVERYTHING NOW");

            // 1. STOP HEARTBEAT IMMEDIATELY
            if (heartbeatStartTimeoutRef.current) {
                clearTimeout(heartbeatStartTimeoutRef.current);
                heartbeatStartTimeoutRef.current = null;
            }
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
                heartbeatIntervalRef.current = null;
            }

            // 2. STOP INACTIVITY TIMER
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
                inactivityTimeoutRef.current = null;
            }

            // 3. STOP CAMERA STREAM BEFORE UNMOUNTING
            try {
                if (window.activeInterviewStream) {
                    window.activeInterviewStream.getTracks().forEach(t => t.stop());
                    window.activeInterviewStream = null;
                    // console.log("CAMERA STREAM STOPPED BEFORE UNMOUNT");
                }
            } catch (err) {
                console.warn("Could not stop camera:", err);
            }

            // 4. Close UI for video module
            setShowVideoModal(false);
            setDestroyVideoModule(true);

            // 5. Flush last chunk
            window.dispatchEvent(new Event("flush-final-video-chunk"));

            // 6. Send final heartbeat
            const heartbeatResponse = await interviewApi.heartbeat({
                question_set: 0,
                isFinish: true,
            });

            const hbStatus = heartbeatResponse?.status;
            if (hbStatus === 0) {
                console.warn("Final heartbeat failed due to network issue.");
            } else if (hbStatus >= 400) {
                console.warn(
                    "Final heartbeat failed:",
                    heartbeatResponse?.message
                );
            }

            /* ──────  End interview (authoritative)────────── */
            // 7. Call backend end API
            const response = await interviewApi.end({ upload_id });
            // console.log("End interview response:", response);
            const { status, code, message } = response || {};

            /* Network error */
            if (status === 0) {
                showSnackbar(
                    "Network error. Please check your internet connection.",
                    "warning"
                );
                return;
            }

            /* Server errors */
            if (status >= 500) {
                showSnackbar(
                    message ||
                    "Server error while submitting interview.",
                    "error"
                );
                return;
            }

            /* Client / validation errors */
            if (status >= 400) {
                showSnackbar(
                    message ||
                    "Failed to end the interview.",
                    "error"
                );
                return;
            }

            /* Success */
            if (status === 200 && code === "success") {
                setIsInterviewCompleted(true);
                navigate("/success", {
                    state: {
                        reason: "interview_completed",
                        message:
                            message ||
                            "Your interview was submitted successfully!",
                    },
                    replace: true,
                });
                return;
            }

            /* Fallback */
            showSnackbar(
                "Unexpected response while ending interview.",
                "error"
            );
        }
        catch (err) {
            console.error("Interview end error:", err);
            showSnackbar(
                "Something went wrong while submitting the interview.",
                "error"
            );
        }
    };

    const handleSubmit = async () => {
        if (!answer || answer.trim() === "") {
            showSnackbar("Answer cannot be empty!", "warning");
            return;
        }

        // ── Language Validation (pre-submit guard) ───────────────────────────
        if (!isAnswerEnglishOnly(answer)) {
            showSnackbar(
                "Your response appears to contain non-English words. Please answer in English only.",
                "error"
            );
            return;
        }
        // ────────────────────────────────────────────────────────────────────

        setContinueLoader(true);
        try {
            const formData = new FormData();
            formData.append("question_id", currentQuestion.id);
            formData.append("candidate_answer", answer);
            formData.append("candidate_id", candidate_id);
            formData.append("recruiter_id", recruiter_id);
            formData.append("plan", "free");

            // formData.append("language", selectedLanguage);
            setContinueLoader(true)

            if (audioBlobRef.current) {
                formData.append(
                    "audio",
                    audioBlobRef.current,
                    `${uuidv4()}.webm`
                );
            }

            const response = await interviewApi.process(formData);
            // console.log("Submit answer response:::", response);
            const { status, code, message } = response || {};

            /* 1️⃣ Network error */
            if (status === 0) {
                showSnackbar(
                    "Network error. Please check your internet connection.",
                    "warning"
                );
                return;
            }

            /* 2️⃣ Server errors */
            if (status >= 500) {
                showSnackbar(
                    message || "Server error while submitting answer.",
                    "error"
                );
                return;
            }

            /* 3️⃣ Client / validation errors */
            if (status >= 400) {
                showSnackbar(
                    message || "Failed to submit answer.",
                    "error"
                );
                return;
            }
            /* 4️⃣ Success */
            if ((status === 200 || status === 201) && code === "success") {
                setCurrentQuestionStartTime(Date.now());
                // setQuestionCount((prev) => Math.max(prev - 1, 0));
                setQuestionCount(questionCount - 1)
                setShowConfirmDialog(false);

                showSnackbar(
                    message || "Answer submitted successfully!",
                    "success"
                );

                /* Move to next question ONLY on success */
                const nextIndex = currentIdx + 1;

                if (nextIndex < questions.length) {
                    setCurrentIdx(nextIndex);
                    setAnswer("");
                    audioBlobRef.current = null;
                } else {
                    setCurrentIdx(-1);
                    await finalizeInterview();
                }

                return;
            }

            /* 5️⃣ Fallback */
            showSnackbar(
                "Unexpected response while submitting answer.",
                "error"
            );

        } catch (error) {
            console.error("Submit answer error:", error);
            showSnackbar(
                "Something went wrong while submitting your answer.",
                "error"
            );
        } finally {
            setContinueLoader(false);
        }
    }


    const getConversationalQuestion = (question = "", index = 0) => {
        if (!question.trim()) return "";

        const intro = CONVERSATION_INTROS[index % CONVERSATION_INTROS.length];
        return `${intro} ${question}`.trim();
    };

    const selectOptimalVoice = () => {
        const synth = window.speechSynthesis;
        const voices = synth.getVoices();
        const exactMatches = voices.filter(voice => voice.lang === selectedAccent);

        if (VOICE_PRIORITIES[selectedAccent] && exactMatches.length > 0) {
            for (const preferredName of VOICE_PRIORITIES[selectedAccent]) {
                const voice = exactMatches.find(v => v.name.includes(preferredName));
                if (voice) return voice;
            }
        }

        return exactMatches[0] || voices.find(v => v.lang.startsWith(selectedAccent.split('-')[0]));
    };

    // Enhanced speech function
    const speakText = (text) => {
        const synth = window.speechSynthesis;

        // Cancel any ongoing speech safely
        if (synth.speaking) {
            synth.cancel();
        }

        // Add a short delay so the first character does NOT get cut off
        setTimeout(() => {
            const voices = synth.getVoices();
            if (voices.length === 0) {
                setTimeout(() => speakText(text), 100);
                return;
            }

            // Add natural pause before starting to avoid cutting words
            const processedText = text;  // 300ms pause
            // const processedText = " . . . " + text;  // 300ms pause

            const utterance = new SpeechSynthesisUtterance(processedText);
            utterance.lang = selectedAccent;
            utterance.rate = 0.85;
            utterance.pitch = 1;

            const selectedVoice = selectOptimalVoice();
            if (selectedVoice) utterance.voice = selectedVoice;

            utterance.onstart = () => setIsQuestionPlaying(true);
            utterance.onend = () => setIsQuestionPlaying(false);
            utterance.onerror = (event) => {
                console.error("Speech synthesis error:", event.error);
                setIsQuestionPlaying(false);
                showSnackbar("Voice synthesis failed.", "error");
            };

            currentUtteranceRef.current = utterance;
            synth.speak(utterance);

        }, 150); // <= This delay prevents the first-char cutoff
    };



    const stopSpeaking = () => {
        const synth = window.speechSynthesis;
        if (synth.speaking) {
            synth.cancel();
            setIsQuestionPlaying(false);
        }
    };


    const startListening = async () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            return showSnackbar("Speech recognition not supported in this browser.", "error");
        }

        if (isQuestionPlaying) {
            showSnackbar("Please wait for the question to finish before recording.", "warning");
            return;
        }

        try {
            // Setup media recording
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            audioChunksRef.current = [];

            mediaRecorderRef.current = new MediaRecorder(stream);
            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                    // console.log("Audio chunk recorded:", event.data.size, "bytes");
                }
            };
            mediaRecorderRef.current.onstop = () => {
                try {
                    if (audioChunksRef.current.length > 0) {
                        audioBlobRef.current = new Blob(audioChunksRef.current, { type: "audio/webm" });
                        // console.log("Audio blob created:", audioBlobRef.current.size, "bytes");
                    }
                } catch (err) {
                    console.error("Failed to create audio blob:", err);
                    audioBlobRef.current = null;
                }
                try { stream.getTracks().forEach(track => track.stop()); } catch (e) { /* noop */ }
            };
            mediaRecorderRef.current.start();
            // console.log("MediaRecorder started");

            // Setup speech recognition
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.lang = selectedAccent;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.continuous = true;

            let finalTranscript = "";
            let restartAttempts = 0;
            const MAX_RESTARTS = 10;

            // console.log("Speech Recognition initialized with continuous=true");

            recognitionRef.current.onstart = () => {
                // console.log("Speech recognition started");
            };

            recognitionRef.current.onresult = (event) => {
                // console.log("onresult fired - resultIndex:", event.resultIndex, "results length:", event.results.length);

                let interimTranscript = "";
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + " ";
                        // console.log("Final transcript segment:", transcript);
                    } else {
                        interimTranscript += transcript;
                        // console.log("Interim transcript segment:", transcript);
                    }
                }

                const combined = finalTranscript + interimTranscript;

                // ── Language Validation (real-time) ──────────────────────────
                if (!isAnswerEnglishOnly(combined)) {
                    console.warn("Non-English / code-mixed transcript detected, clearing.", combined);
                    // Reset accumulated transcripts so non-English text is not stored
                    finalTranscript = "";
                    showSnackbar(
                        "Please respond in English only. Responses in other languages are not accepted.",
                        "warning"
                    );
                    setAnswer("");
                    return;
                }
                // ─────────────────────────────────────────────────────────────

                // console.log("Setting answer to:", combined);
                setAnswer(combined);
            };

            recognitionRef.current.onerror = (event) => {
                console.error("Speech recognition error:", event.error);

                const criticalErrors = ['not-allowed', 'permission-denied', 'service-not-allowed'];
                if (criticalErrors.includes(event.error)) {
                    showSnackbar(`Microphone error: ${event.error}`, "error");
                    stopListening();
                    return;
                }

                // For transient errors attempt to restart recognition
                if (restartAttempts < MAX_RESTARTS && listening) {
                    restartAttempts++;
                    // console.log("Attempting recognition restart, attempt:", restartAttempts);
                    setTimeout(() => {
                        try {
                            if (recognitionRef.current && listening) {
                                recognitionRef.current.start();
                            }
                        } catch (err) {
                            console.warn("Failed to restart recognition:", err);
                        }
                    }, 250);
                } else if (listening) {
                    console.warn("Max restart attempts reached or listening stopped");
                    showSnackbar("Speech recognition repeatedly failed. Please try again.", "error");
                    stopListening();
                }
            };

            recognitionRef.current.onend = () => {
                // console.log("Speech recognition onend fired");

                // Check the current listening state directly from the ref to ensure we have the latest value
                // Only restart if we're still supposed to be listening (user hasn't clicked Next/Stop)
                setTimeout(() => {
                    // Doublecheck listening state after a brief delay
                    setListening(currentListening => {
                        // console.log("onend: checking listening state =", currentListening);
                        if (currentListening && recognitionRef.current) {
                            try {
                                // console.log("Restarting speech recognition from onend");
                                recognitionRef.current.start();
                            } catch (err) {
                                console.warn("Failed to restart recognition:", err);
                            }
                        }
                        return currentListening;
                    });
                }, 100);
                // Keep restarting recognition while listening is true
                if (listening) {
                    setTimeout(() => {
                        try {
                            if (recognitionRef.current) {
                                // console.log("Restarting speech recognition from onend");
                                recognitionRef.current.start();
                            }
                        } catch (err) {
                            console.warn("Failed to restart recognition:", err);
                        }
                    }, 100);
                }
            };

            setListening(true);
            recognitionRef.current.start();
            // console.log("Speech recognition started");

        } catch (error) {
            showSnackbar("Microphone access denied.", "error");
            console.error("Microphone error:", error);
        }
    };



    const stopListening = () => {
        // console.log("stopListening called, current listening state:", listening);

        // Set listening to false FIRST to prevent onend from restarting
        setListening(false);

        // Stop speech recognition but keep audioChunks for playback
        try {
            if (recognitionRef.current) {
                // Remove event handlers before stopping to prevent auto-restart
                recognitionRef.current.onresult = null;
                recognitionRef.current.onend = null;
                recognitionRef.current.onerror = null;
                recognitionRef.current.stop();
                // console.log("Speech recognition stopped");
            }
        } catch (e) {
            console.warn("Recognition stop error:", e);
        }

        // Stop media recorder if recording
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
                mediaRecorderRef.current.stop();
                // console.log("MediaRecorder stopped");
            }
        } catch (e) {
            console.warn("mediaRecorder stop failed:", e);
        }

        clearTimeout(speechTimeoutRef.current);

        // Set listening to false AFTER stopping to prevent any async restarts
        setListening(false);
    };


    const handleReattempt = () => {
        setAnswer("");
        audioBlobRef.current = null;
        setShowConfirmDialog(false);
        setConversationState('listening');
        setTimeout(() => startListening(), 500);
    };


    const handleMoveForward = async () => {
        await handleSubmit();
    };

    // handler to stop mic and open confirmation dialog manually
    const handleNext = () => {
        if (!listening) {
            if (answer.trim()) {
                setConversationState('answered');
                setShowConfirmDialog(true);
            } else {
                showSnackbar("No response captured yet.", "info");
            }
            return;
        }

        // Stop listening and wait briefly for onstop to produce audioBlob
        stopListening();
        setConversationState('answered');

        // Wait briefly to allow mediaRecorder.onstop to finalize audioBlob then open dialog
        setTimeout(() => {
            setShowConfirmDialog(true);
        }, 350);
    };


    // stop current audio playback and cleanup
    const stopPlayback = useCallback(() => {
        // console.log("stopPlayback called");
        try {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                // revoke URL if we created one
                if (audioRef.current._srcUrl) {
                    URL.revokeObjectURL(audioRef.current._srcUrl);
                }
                audioRef.current = null;
                // console.log("Audio stopped and cleaned up");
            }
        } catch (err) {
            console.warn("stopPlayback error:", err);
        } finally {
            setIsPlayingAudio(false);
        }
    }, []);

    const playRecordedAudio = () => {
        try {
            // console.log("playRecordedAudio called, isPlayingAudio:", isPlayingAudio);

            // If already playing -> stop
            if (isPlayingAudio) {
                // console.log("Stopping playback...");
                stopPlayback();
                return;
            }

            // prefer finalized blob; fallback to current chunks while still recording
            let blob = audioBlobRef.current;
            if (!blob && audioChunksRef.current && audioChunksRef.current.length > 0) {
                try {
                    blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                    // console.log("Created transient blob from chunks:", blob.size, "bytes");
                } catch (e) {
                    console.error("Failed to create transient blob for playback:", e);
                    blob = null;
                }
            }

            if (!blob) {
                console.warn("No blob available for playback");
                showSnackbar("No recording available yet.", "info");
                return;
            }

            // console.log("Creating audio from blob, size:", blob.size);
            const audioUrl = URL.createObjectURL(blob);
            const audio = new Audio(audioUrl);
            audio._srcUrl = audioUrl;
            audioRef.current = audio;

            audio.play().then(() => {
                // console.log("Audio playback started successfully");
                setIsPlayingAudio(true);
            }).catch((err) => {
                console.error("Playback failed:", err);
                showSnackbar("Failed to play audio.", "error");
                stopPlayback();
            });

            audio.onended = () => {
                // console.log("Audio playback ended naturally");
                stopPlayback();
            };

        } catch (err) {
            console.error("playRecordedAudio error:", err);
            showSnackbar("Playback error.", "error");
            stopPlayback();
        }
    };

    // Ensure playback stops when dialog closes or component unmounts
    useEffect(() => {
        if (!showConfirmDialog) {
            // console.log("Dialog closed, stopping playback");
            stopPlayback();
        }
        //    }, [showConfirmDialog, stopPlayback]);
    }, [showConfirmDialog]);

    useEffect(() => {
        return () => {
            // cleanup playback on unmount
            stopPlayback();
            stopListening();
            stopSpeaking();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);



    // Determine if the recording button should be disabled
    const isRecordingDisabled = isQuestionPlaying || listening;

    // Effects
    useEffect(() => {
        if (!hasFetchedRef.current) {
            hasFetchedRef.current = true;
            fetchQuestions();
            setShowVideoModal(true);
        }
    }, []);

    useEffect(() => {
        if (questions.length > 0 && currentIdx >= 0 && currentIdx < questions.length && voicesLoaded) {
            const questionText = getConversationalQuestion(questions[currentIdx].question, currentIdx);
            setTimeout(() => speakText(questionText), 1000);
        }
    }, [currentIdx, selectedAccent, voicesLoaded]);


    useEffect(() => {
        // CLEANUP ANY OLD TIMEOUT
        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
            inactivityTimeoutRef.current = null;
        }

        if (questions.length === 0 || isInterviewCompleted) {
            return;
        }

        const handleInactivity = async () => {
            setIsInterviewCompleted(true);

            try {
                const response = await interviewApi.inactive({
                    question_set: questionCount,
                });

                // console.log("Inactivity API response:", response);

                const { status, message } = response || {};

                /* Network error */
                if (status === 0) {
                    console.warn(
                        "Network error while notifying inactivity."
                    );
                }
                /* Server / client error */
                else if (status >= 400) {
                    console.warn(
                        "Failed to mark interview inactive:",
                        message
                    );
                }
                /* Success */
                else if (status === 200) {
                    // console.log("Interview marked inactive successfully.");
                }
            } catch (error) {
                console.error(
                    "Exception while notifying inactivity:",
                    error
                );
            }
            finally {
                /* 🚨 Navigation must happen regardless */
                navigate("/restriction", {
                    state: {
                        reason: "inactive_long_time",
                    },
                    replace: true,
                });
            }
        };

        const resetTimer = () => {
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
            }
            inactivityTimeoutRef.current = setTimeout(handleInactivity, 120000); // 4 minutes
        };

        // Events to detect activity
        window.addEventListener("mousemove", resetTimer);
        window.addEventListener("keydown", resetTimer);
        window.addEventListener("click", resetTimer);
        window.addEventListener("scroll", resetTimer);
        window.addEventListener("touchstart", resetTimer);

        resetTimer(); // start timer

        return () => {
            window.removeEventListener("mousemove", resetTimer);
            window.removeEventListener("keydown", resetTimer);
            window.removeEventListener("click", resetTimer);
            window.removeEventListener("scroll", resetTimer);
            window.removeEventListener("touchstart", resetTimer);

            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
                inactivityTimeoutRef.current = null;
            }
        };
    }, [questions, isInterviewCompleted, questionCount]);


    useEffect(() => {
        // Clean previous interval and start-timeout
        if (heartbeatStartTimeoutRef.current) {
            clearTimeout(heartbeatStartTimeoutRef.current);
            heartbeatStartTimeoutRef.current = null;
        }
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }

        if (questions.length === 0 || isInterviewCompleted) {
            return;
        }

        const INTERVAL_MS = 10000; // 10 seconds

        heartbeatStartTimeoutRef.current = setTimeout(() => {
            heartbeatIntervalRef.current = setInterval(async () => {
                try {
                    const heartbeatResponse = await interviewApi.heartbeat({
                        question_set: 0,
                        isFinish: true,
                    });

                    const hbStatus = heartbeatResponse?.status;
                    if (hbStatus === 0) {
                        console.warn("Final heartbeat failed due to network issue.");
                    } else if (hbStatus >= 400) {
                        console.warn(
                            "Final heartbeat failed:",
                            heartbeatResponse?.message
                        );
                    }

                } catch (error) {
                    console.error("Heartbeat failed:", error);
                    clearInterval(heartbeatIntervalRef.current);
                    heartbeatIntervalRef.current = null;
                }
            }, INTERVAL_MS);
        }, 2000); // slight startup delay

        return () => {
            if (heartbeatStartTimeoutRef.current) {
                clearTimeout(heartbeatStartTimeoutRef.current);
                heartbeatStartTimeoutRef.current = null;
            }
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
                heartbeatIntervalRef.current = null;
            }
        };
    }, [questions, isInterviewCompleted]);

    // Loading state
    const currentQuestion = questions[currentIdx];
    // const isInterviewComplete = currentIdx === -1;
    if (!questions.length) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f5f7fa">
                <CircularProgress size={50} sx={{ mb: 2, color: '#1976d2' }} />
                <Typography variant="h6" color="text.secondary">Loading interview questions...</Typography>
            </Box>
        );
    }

    if (destroyVideoModule) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
            >
                <Typography variant="h6">Finalizing interview...</Typography>
            </Box>
        );
    }




    return (
        <Box sx={{ minHeight: "100vh", py: 4, alignItems: "center", backgroundColor: "#e8eaed" }}>
            <Container maxWidth="lg" >

                {!destroyVideoModule && (
                    <VideoRecorderModal
                        open={showVideoModal}
                        candidateId={candidate_id}
                        onClose={(uploadedUrl) => {
                            // console.log("Video uploaded:", uploadedUrl);
                            setVideoUrl(uploadedUrl);
                        }}
                    />
                )}
                <CustomCard
                    title={
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <img
                                src={cybermeru}
                                alt="CyberMeru"
                                style={{
                                    width: 150, height: 60, objectFit: 'contain'
                                }}
                            />


                        </Stack>
                    }
                    // subtitle={`Question ${currentIdx + 1} of ${questions.length}`}
                    icon={SmartToy}
                    size={{ height: "50%", width: "100%" }}
                    color={{ main: "#000000ff", light: "#ff0000ff" }}
                    contentPadding={false}
                    contentStyle={{ p: 0 }}
                    sx={{
                        backgroundColor: "white"
                    }}
                >
                    <Box sx={{ p: 3, background: "rgba(74, 74, 74, 0.05)" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Stack direction="row" spacing={1}>
                                <Chip
                                    label={currentQuestion.skill || "General"}
                                    disabled
                                    sx={{
                                        bgcolor: "#ff7043 !important",
                                        color: "#ffffffff !important",
                                        fontWeight: 700,
                                        opacity: "1 !important",
                                        "& .MuiChip-label": {
                                            color: "#ffffffff !important",
                                        },
                                    }}
                                />
                            </Stack>

                            {/* NEW ACCENT SELECTOR */}
                            <FormControl size="small" sx={{ minWidth: 160, bgcolor: 'white', borderRadius: 1 }}>
                                <InputLabel id="accent-select-label">Voice Accent</InputLabel>
                                <Select
                                    labelId="accent-select-label"
                                    value={selectedAccent}
                                    label="Voice Accent"
                                    onChange={(e) => setSelectedAccent(e.target.value)}
                                >
                                    {Object.entries(ACCENT_OPTIONS).map(([code, name]) => (
                                        <MenuItem key={code} value={code}>
                                            {name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                        </Stack>
                    </Box>

                    <Divider sx={{ borderColor: "rgba(109, 66, 66, 0.1)" }} />

                    <Box sx={{ p: 4, color: "text.primary" }}>
                        <Paper sx={{ p: 2, mb: 3, bgcolor: "#f9f9f9", borderRadius: 2, border: "2px solid #e0e0e0" }}>
                            <Stack direction="row" alignItems="flex-start" spacing={2} mb={2}>
                                <Avatar sx={{ bgcolor: "#1976d2", width: 36, height: 36 }}>
                                    <RecordVoiceOver fontSize="small" />
                                </Avatar>
                                <Box flex={1}>
                                    <Typography variant="subtitle1" fontWeight={700} fontSize={"1.5rem"} color="#1976d2" mb={1}>
                                        Interview Question1
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500} sx={{ lineHeight: 1.8, fontSize: "1.1rem", color: "#000000ff" }}>
                                        {getConversationalQuestion(currentQuestion.question, currentIdx)}
                                    </Typography>
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <IconButton onClick={() => speakText(getConversationalQuestion(currentQuestion.question, currentIdx))} disabled={isQuestionPlaying || listening} sx={{ bgcolor: (isQuestionPlaying || listening) ? "grey.300" : "#030303ff", color: "white", '&:hover': { bgcolor: (isQuestionPlaying || listening) ? "grey.400" : "#424242" } }} size="small">
                                    <VolumeUp fontSize="small" />
                                </IconButton>
                            </Stack>
                        </Paper>

                        <Paper sx={{ p: 2, mb: 3, bgcolor: isRecordingDisabled ? "#f5f5f5" : "#fafafa", border: `2px solid ${isRecordingDisabled ? "#e0e0e0" : "#9e9e9e"}`, borderRadius: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{
                                    bgcolor: isRecordingDisabled ? "grey.400" : (listening ? "#616161" : "#757575"),
                                    width: 32,
                                    height: 32,
                                    animation: listening ? "pulse 1.5s infinite" : "none"
                                }}>
                                    {listening ? <Mic fontSize="small" /> : <MicOff fontSize="small" />}
                                </Avatar>
                                <Box flex={1}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {isQuestionPlaying
                                            ? "Question is being read - Please wait..."
                                            : listening
                                                ? "Recording your response..."
                                                : "Ready to record your answer"
                                        }
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {isQuestionPlaying
                                            ? "Wait for the question to finish, then click to start recording"
                                            : listening
                                                ? "Speak clearly - I'm listening and transcribing in real-time"
                                                : "Click the microphone to start recording your response"
                                        }
                                    </Typography>
                                </Box>
                                <CustomButton
                                    onClick={startListening}
                                    variant="contained"
                                    size="medium"
                                    startIcon={<Mic />}
                                    disabled={isRecordingDisabled}
                                    sx={{
                                        minWidth: 140,
                                        '&.Mui-disabled': {
                                            color: 'white',
                                            opacity: 0.6
                                        }
                                    }}
                                >
                                    {isQuestionPlaying ? "Please Wait" : listening ? "Recording..." : "Start Recording"}
                                </CustomButton>

                                <CustomButton
                                    onClick={handleNext}
                                    variant={listening ? "contained" : "outlined"}
                                    size="medium"
                                    startIcon={<ArrowForward />}
                                    disabled={!listening && !answer}
                                    sx={{
                                        ml: 1,
                                        minWidth: 120
                                    }}
                                >
                                    Finish Answer
                                </CustomButton>
                            </Stack>
                        </Paper>

                        {answer && (
                            <Fade in={!!answer}>
                                <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: "#fafafa", border: "2px solid #9e9e9e", borderRadius: 2 }}>
                                    <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                        <Avatar sx={{ bgcolor: "#616161", width: 32, height: 32 }}>
                                            <CheckCircle fontSize="small" />
                                        </Avatar>
                                        <Typography variant="subtitle1" fontWeight={600} color="#4a4a4a">
                                            Your Response
                                        </Typography>
                                        {audioBlobRef.current && (
                                            <IconButton onClick={playRecordedAudio} size="small" sx={{ bgcolor: "#757575", color: "white", '&:hover': { bgcolor: "#616161" } }}>
                                                <PlayArrow fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Stack>
                                    <Paper sx={{ p: 2, bgcolor: "white", borderLeft: "4px solid #757575" }}>
                                        <Typography sx={{ fontStyle: "italic", lineHeight: 1.6 }}>
                                            "{answer}"
                                        </Typography>
                                    </Paper>
                                </Paper>
                            </Fade>
                        )}
                    </Box>
                </CustomCard>

                <Dialog open={showConfirmDialog} onClose={() => { setShowConfirmDialog(false); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                    <DialogContent sx={{ textAlign: "center", py: 4 }}>
                        <Avatar sx={{ bgcolor: "#616161", width: 60, height: 60, mx: "auto", mb: 3 }}>
                            <CheckCircle sx={{ fontSize: 30 }} />
                        </Avatar>
                        <Typography variant="h6" fontWeight={600} mb={2}>
                            Great! I've captured your response.
                        </Typography>
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            Would you like to re-attempt this question or move forward to the next one?
                        </Typography>

                        {/* Play recorded audio if available - SINGLE BUTTON */}
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mb: 2 }}>
                            <CustomButton
                                onClick={playRecordedAudio}
                                variant={isPlayingAudio ? "contained" : "outlined"}
                                startIcon={isPlayingAudio ? <Stop /> : <PlayArrow />}
                                disabled={!(audioBlobRef.current || (audioChunksRef.current && audioChunksRef.current.length > 0))}
                                sx={{
                                    minWidth: 160,
                                }}
                            >
                                {isPlayingAudio ? "Stop Playing" : "Play Recording"}
                            </CustomButton>
                            {!audioBlobRef.current && !(audioChunksRef.current && audioChunksRef.current.length > 0) && (
                                <Typography variant="caption" color="text.secondary">Recording will be available shortly after you stop.</Typography>
                            )}
                        </Box>

                        {answer && (
                            <Paper sx={{ p: 2, mb: 3, bgcolor: "#f8f9fa", textAlign: "left" }}>
                                <Typography variant="body2" color="text.secondary" mb={1}>Your answer:</Typography>
                                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                                    "{answer.length > 100 ? answer.substring(0, 100) + "..." : answer}"
                                </Typography>
                            </Paper>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 2 }}>
                        <CustomButton onClick={handleReattempt} disabled={continueLoader} variant="contained" size="large"
                            startIcon={<Replay />}
                            sx={{ width: "40%", height: "7vh" }}>
                            Re-attempt
                        </CustomButton>

                        <CustomButton onClick={handleMoveForward}
                            size="large"
                            variant="contained"
                            disabled={continueLoader}
                            sx={{ width: "40%", height: "7vh" }}>
                            {continueLoader ? (
                                <DotsLoader color="black" />
                            ) : (
                                <>
                                    Submit answer <ArrowForward sx={{ ml: 1 }} />
                                </>
                            )}
                        </CustomButton>
                    </DialogActions>
                </Dialog>

                {/* Refresh / Close warning dialog */}
                <Dialog open={showRefreshDialog} onClose={() => setShowRefreshDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                    <DialogContent sx={{ textAlign: "center", py: 4 }}>
                        <Avatar sx={{ bgcolor: "#616161", width: 60, height: 60, mx: "auto", mb: 3 }}>
                            <Stop sx={{ fontSize: 30 }} />
                        </Avatar>
                        <Typography variant="h6" fontWeight={600} mb={2}>
                            Are you sure?
                        </Typography>
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            Refreshing or closing this page will terminate the interview session. Do you want to continue?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 2 }}>
                        <CustomButton onClick={() => setShowRefreshDialog(false)} variant="contained" size="large"
                            sx={{ width: "40%", height: "7vh" }}>
                            Cancel
                        </CustomButton>

                        <CustomButton onClick={async () => {
                            setIsFinalizingForRefresh(true);
                            try {
                                await finalizeInterview();
                            } catch (err) {
                                console.error('Error finalizing before refresh:', err);
                            }
                            window.location.reload();
                        }}
                            size="large"
                            variant="contained"
                            disabled={isFinalizingForRefresh}
                            sx={{ width: "40%", height: "7vh" }}>
                            {isFinalizingForRefresh ? 'Ending...' : 'Continue'}
                        </CustomButton>
                    </DialogActions>
                </Dialog>

                <CustomSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.type}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                />

                {videoUrl && (
                    <p style={{ marginTop: 20 }}>Video uploaded: {videoUrl}</p>
                )}
            </Container>
            {/* <Footer />   */}
        </Box>
    );
};

export default InterviewPage;